/* eslint-disable jsx-a11y/anchor-is-valid */
import SVG from 'react-inlinesvg'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {toAbsoluteUrl} from '../../../../template/_metronic/helpers'
import {useLoginMutation} from '../../../../redux/api/services/auth'
import {Link} from 'react-router-dom'
import {InputPassword} from './input/InputPassword'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../redux/store'
import {CustomErrorAlert} from '../../../../libs/_fri/components/CustomErrorAlert/CustomErrorAlert'
import {getStorage, removeStorage, setStorage} from '../../../../helpers/utilities'
import {LoginRequest} from '../../../../redux/api/interfaces/auth'
import {useEffect} from 'react'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Correo electrónico inválido.')
    .required('Correo electrónico es requerido'),
  password: Yup.string()
    .matches(/^\S*$/, 'La contraseña no puede contener espacios')
    .required('Se requiere contraseña'),
})

const initialValues = {
  email: '',
  password: '',
  rememberMe: false,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [login, {isError, isLoading, error, reset}] = useLoginMutation()
  const infoState = useSelector((state: RootState) => state.info)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus}) => {
      if (values.rememberMe) {
        setStorage(
          process.env.REACT_APP_AUTH_REMEMBERME_LOCAL_STORAGE_KEY || '',
          values.email,
          10368000
        )
      } else {
        removeStorage(process.env.REACT_APP_AUTH_REMEMBERME_LOCAL_STORAGE_KEY || '')
      }

      const dataRequest: LoginRequest = {
        info: infoState,
        requestContent: {
          email: values.email,
          password: values.password,
        },
      }
      login(dataRequest)
    },
  })

  useEffect(() => {
    const savedEmail = getStorage(process.env.REACT_APP_AUTH_REMEMBERME_LOCAL_STORAGE_KEY || '')
    if (savedEmail) {
      formik.setFieldValue('email', savedEmail)
      formik.setFieldValue('rememberMe', true)
    }
  }, [])

  return (
    <form
      className='form w-100 py-10 py-lg-29'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='d-flex flex-column align-items-center mb-10'>
        <SVG src={toAbsoluteUrl('/media/soyfri-media/logo-planillas.svg')} className='mb-8' />
        <SVG src={toAbsoluteUrl('/media/soyfri-media/login-separator.svg')} className='mb-7' />
        <h1 className='text-primary-dark fs-2hx'>Inicio de sesión</h1>
      </div>
      {/* begin::Heading */}

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <CustomErrorAlert show={isError} error={error} close={reset} />

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label text-primary-dark fs-3'>Correo electrónico</label>
        <input
          placeholder='correo@ejemplo.com'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg form-control-solid text-primary-dark border-0 fs-3',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-6'>
        <label className='form-label text-primary-dark fs-3'>Contraseña</label>

        <InputPassword
          {...formik.getFieldProps('password')}
          className={clsx(
            '',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />

        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      <div className='form-check form-check-custom form-check-solid mb-8'>
        <input
          className='form-check-input'
          type='checkbox'
          id='rememberMe'
          checked={formik.values.rememberMe}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isLoading}
        />
        <label className='form-check-label fw-semibold text-primary-dark fs-3' htmlFor='rememberMe'>
          Recordarme
        </label>
      </div>

      {/* begin::Action */}
      <div className='d-grid mb-8'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg btn-primary rounded-pill'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!isLoading && <span className='indicator-label fs-4'>Continuar</span>}
          {isLoading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Espere porfavor...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      <div className='text-center'>
        <Link to='/auth/forgot-password' className='text-primary fw-semibold fs-4'>
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
      {/* end::Action */}
    </form>
  )
}
